// Curated Oracle keyword list emitted by `oracleResolver`. Tier 7 per
// RESEARCH.md §5.2. The orchestrator's `applyKeywordCasingPostProcess`
// transforms label/insertText/filterText to the user's preferred casing —
// labels stay UPPER here so the dominant-case fallback has a stable
// reference and matches Oracle's conventional rendering.
//
// Selection criteria: Oracle SQL (and a handful of PL/SQL block-level
// keywords) that surface in normal user authoring flows. PL/SQL semantic
// keywords (BEGIN, EXCEPTION, etc.) are the SemanticResolverReadOnly
// surface; here we list only the SQL-layer keywords plus the small set
// PL/SQL users type at SQL*Plus / SQLcl prompts.
//
// `DUAL` is intentionally NOT in this list — it's emitted as a built-in
// pseudo-relation in `oracleResolver.ts` (FROM-position only) so it
// surfaces with kind=table, not kind=keyword. Putting it in both places
// would emit duplicate suggestions.

const ORACLE_KEYWORD_LIST: readonly string[] = [
  // DML
  'SELECT', 'FROM', 'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'CROSS',
  'FULL', 'OUTER', 'NATURAL', 'ON', 'USING', 'GROUP', 'BY', 'HAVING',
  'ORDER', 'ASC', 'DESC',
  'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE', 'MERGE',
  'FETCH', 'FIRST', 'ROWS', 'ONLY', 'NEXT', 'PERCENT', 'WITH', 'TIES',
  'OFFSET', 'RETURNING',
  // DDL
  'CREATE', 'TABLE', 'VIEW', 'INDEX', 'SEQUENCE', 'SYNONYM', 'TYPE', 'ROWTYPE',
  'MATERIALIZED', 'DATABASE', 'LINK', 'DIRECTORY', 'TABLESPACE',
  'DROP', 'ALTER', 'RENAME', 'TRUNCATE', 'COMMENT',
  // Query structure
  'AS', 'DISTINCT', 'UNION', 'ALL', 'INTERSECT', 'MINUS', 'EXISTS', 'IN',
  'LIKE', 'BETWEEN', 'IS', 'NULL', 'NOT', 'AND', 'OR',
  'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  // Date/time literal + interval-unit keywords
  'INTERVAL', 'DAY', 'MONTH', 'YEAR', 'HOUR', 'MINUTE', 'SECOND',
  // PL/SQL block-level
  'DECLARE', 'BEGIN', 'EXCEPTION', 'CURSOR', 'LOOP', 'FOR', 'WHILE',
  'IF', 'ELSIF', 'RETURN', 'RAISE',
  'EXECUTE', 'IMMEDIATE',
  // Constraints & keys
  'PRIMARY', 'KEY', 'FOREIGN', 'REFERENCES', 'UNIQUE', 'CHECK', 'DEFAULT',
  'CONSTRAINT', 'CASCADE', 'RESTRICT',
  // Transactions
  'COMMIT', 'ROLLBACK', 'SAVEPOINT',
  // Privileges
  'GRANT', 'REVOKE', 'TO', 'PUBLIC', 'ADMIN', 'OPTION',
  // Oracle-specific keywords
  'ROWNUM', 'ROWID', 'LEVEL', 'CONNECT', 'START', 'PRIOR',
  'NOCACHE', 'CACHE', 'CYCLE', 'NOCYCLE', 'INCREMENT', 'MINVALUE', 'MAXVALUE',
  // Sequence options (ORDER is already in DML above). NOMINVALUE / NOMAXVALUE
  // / NOORDER / KEEP / NOKEEP / SCALE / NOSCALE / SHARING / SESSION / GLOBAL
  // surface at the CREATE SEQUENCE `...` slot. Tier 7 across the board — Oracle
  // users almost always type sequence options deliberately, so leaking them at
  // unrelated positions is a non-issue.
  'NOMINVALUE', 'NOMAXVALUE', 'NOORDER', 'KEEP', 'NOKEEP',
  'SCALE', 'NOSCALE', 'SHARING', 'SESSION', 'GLOBAL',
  'NEXTVAL', 'CURRVAL',
  'PARTITION', 'SUBPARTITION', 'RANGE', 'LIST', 'HASH',
  'EXPLAIN', 'PLAN',
  // Package/procedure (DDL-side keywords only — body keywords come from PL/SQL).
  'PACKAGE', 'BODY', 'PROCEDURE', 'FUNCTION', 'TRIGGER',
  'REPLACE', 'FORCE', 'EDITIONABLE', 'NONEDITIONABLE',
  // Oracle 23ai / 26ai — accepted only by 23ai-family servers (19c rejects them)
  'QUALIFY', 'VECTOR', 'ASSERTION',
];

export const ORACLE_KEYWORD_SET: ReadonlySet<string> = new Set(ORACLE_KEYWORD_LIST);

/**
 * Position-aware keyword list. Today returns the whole curated set — the
 * orchestrator's quality-filter trims the popup. A future per-position
 * refinement can split this into clause-start vs general subsets.
 */
export function getOracleKeywordsForPosition(): readonly string[] {
  return ORACLE_KEYWORD_LIST;
}

/**
 * Multi-word Oracle keywords typed one word at a time — see
 * `compoundKeywordRemainder.ts`. Only compounds
 * `context/legalNextKeywords.ts` does not already carry for oracle belong
 * here. Sources:
 *   • Materialized-view refresh clause (Oracle SQL Language Reference,
 *     `CREATE MATERIALIZED VIEW`): the refresh triggers and the
 *     data-dependency / schedule anchors the resolver emits whole at
 *     `REFRESH ▮` (`ORACLE_MV_REFRESH_KEYWORDS` in `oracleResolver.ts`,
 *     coverage script.32 §5) — nothing completed them once `ON ` was written
 *     (`REFRESH ON D▮`).
 *   • `UNBOUNDED FOLLOWING` — window frame END bound (`analytic_function`
 *     windowing clause); the per-slot model carries only the frame-START
 *     vocabulary and the direction-scoped lists live in `oracleResolver.ts`
 *     (`ORACLE_FRAME_START_KEYWORDS` / `ORACLE_FRAME_END_KEYWORDS`).
 */
export const ORACLE_COMPOUND_KEYWORDS: readonly string[] = [
  'ON DEMAND',
  'ON COMMIT',
  'WITH PRIMARY KEY',
  'WITH ROWID',
  'START WITH',
  'UNBOUNDED FOLLOWING',
];
